import * as React from 'react';
import { motion } from 'framer-motion';
import { graphql } from 'gatsby';
import { renderRichText } from 'gatsby-source-contentful/rich-text';
import styled from 'styled-components';

import { GradientHeader } from '@/components/design-system';
import { CustomerBanner } from '@/components/global/CustomerBanner';
import DefaultForm from '@/components/global/DefaultForm';
import { richTextLarge } from '@/components/global/RichText';
import { SEO } from '@/components/global/SEO';
import Page from '@/components/v2/Page';
import { V1GridLines, V1PageWrapper } from '@/components/v2/Page/V1PageWrapper';
import { INTERNAL_LINKS } from '@/utils';

const Wrapper = styled(motion.div)`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  padding: 0 40px;
  margin-bottom: 40px;
`;

const Inner = styled.div`
  width: 100%;
  max-width: ${({ theme }) => theme.maxWidth.PAGE};
`;

const Grid = styled.div`
  &[data-include-form='true'] {
    display: grid;
    grid-template-columns: 3fr 1fr;
    grid-gap: 24px;

    @media (max-width: ${({ theme }) => theme.mediaQuery.LARGE}) {
      grid-template-columns: 1fr;
    }
  }

  .default-form-confirmation-message {
    margin: auto;
    padding: 0px !important;

    @media (max-width: ${({ theme }) => theme.mediaQuery.LARGE}) {
      padding: 40px !important;
    }
  }

  div:has(> form) {
    padding: 16px !important;

    @media (max-width: ${({ theme }) => theme.mediaQuery.LARGE}) {
      padding: 40px !important;
    }
  }

  form {
    grid-template-columns: 1fr;
    @media (max-width: ${({ theme }) => theme.mediaQuery.LARGE}) {
      grid-template-columns: repeat(2, 1fr);
    }
  }
`;

const Top = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  max-width: 800px;
  padding: 0 30px;
  margin: 0 auto 60px auto;
`;

const Title = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 10px;
  gap: 15px;

  @media (max-width: ${({ theme }) => theme.mediaQuery.MEDIUM}) {
    gap: 10px;

    svg {
      width: 40px;
      height: auto;
    }
  }
`;

const VideoWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  margin-bottom: 30px;
  width: 100%;
`;

const Video = styled.iframe`
  aspect-ratio: 16 / 9;
  width: 100%;
  border: none;
`;

interface TechTalkVideoTemplateProps {
  data: {
    contentfulGatedContent?: {
      description: string;
      gatedDefaultFormId?: string;
      slug: string;
      socialImage?: {
        url: string;
      };
      title: string;
      videoUrl: string;
    };
    contentfulHowToHex?: {
      socialImage?: {
        url: string;
      };
    };
    contentfulTechTalk?: {
      metaDescription?: string;
      ogImage?: {
        url: string;
      };
    };
    route: {
      description: {
        raw: string;
      };
      slug: string;
      title: string;
      videoUrl: string;
    };
  };
}

const TechTalkVideoTemplate: React.ComponentType<TechTalkVideoTemplateProps> =
  React.memo(({ data }) => {
    const { title, videoUrl, slug } =
      data.route || data.contentfulHowToHex || data.contentfulGatedContent;
    const description = data.route?.description;
    const metaDescription = data.contentfulTechTalk?.metaDescription || '';
    const metaImage =
      data.contentfulTechTalk?.ogImage?.url ||
      data.contentfulHowToHex?.socialImage?.url ||
      '';

    const rawDescription = description?.raw;

    const isFormIncluded = Boolean(
      data.contentfulGatedContent?.gatedDefaultFormId
    );

    return (
      <Page
        seo={
          <SEO
            description={metaDescription}
            image={metaImage}
            noIndex={true}
            title={title}
            url={`${INTERNAL_LINKS.Watch}${slug}/`}
          />
        }>
        <V1GridLines>
          <V1PageWrapper>
            <Wrapper>
              <Inner>
                <Top>
                  <Title>
                    <GradientHeader renderAs="h1">{title}</GradientHeader>
                  </Title>
                  {rawDescription
                    ? renderRichText(
                        {
                          raw: rawDescription,
                          references: []
                        },
                        richTextLarge
                      )
                    : null}
                  {data.contentfulGatedContent?.description ? (
                    <p>{data.contentfulGatedContent.description}</p>
                  ) : null}
                </Top>
                <Grid data-include-form={isFormIncluded}>
                  <VideoWrapper>
                    <Video
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                      allowFullScreen={true}
                      src={videoUrl}
                      title={title}></Video>
                  </VideoWrapper>
                  {data.contentfulGatedContent?.gatedDefaultFormId ? (
                    <DefaultForm
                      formId={data.contentfulGatedContent.gatedDefaultFormId}
                      mediaToOpen={undefined}
                      slug={slug}
                    />
                  ) : null}
                </Grid>
              </Inner>
              <CustomerBanner minimal={true} />
            </Wrapper>
          </V1PageWrapper>
        </V1GridLines>
      </Page>
    );
  });

export const pageQuery = graphql`
  query ($slug: String!, $eventSlug: String!) {
    route: contentfulTechTalk(slug: { eq: $slug }) {
      ...BlockContentfulTechTalkVideos
    }
    contentfulTechTalk(event: { elemMatch: { url: { eq: $eventSlug } } }) {
      metaDescription
      ogImage {
        url
      }
    }
    contentfulHowToHex(slug: { eq: $slug }) {
      socialImage {
        url
      }
      slug
      videoUrl
      title
    }

    contentfulGatedContent(slug: { eq: $slug }) {
      socialImage: socialShareImage {
        url
      }
      slug
      description: gatedEmbedDescription
      videoUrl: gatedEmbed
      title: gatedEmbedTitle
      gatedDefaultFormId
    }
  }
`;

export default TechTalkVideoTemplate;
